/**
 * ʕ•̫͡·•ʔ੭
 * A generic floating OverlayBanner component that appears fixed at the top or bottom of the screen.
 *
 * The banner is composed using children, allowing consumers to mix normal and bold text,
 * add action icons, dividers, or any other elements — without prescribing the content via props.
 *
 * @param position    - Where the banner anchors: 'top' or 'bottom'. Defaults to 'bottom'.
 * @param icon        - Optional leading icon element (e.g. a colored SVG icon).
 * @param children    - Banner content: text, bold spans, action icons, dividers, etc.
 * @param offset      - Optional CSS offset from the edge (e.g. '2rem'). Defaults to '2.4rem'.
 * @param sx          - Optional MUI sx overrides for the root container.
 */
import { SxProps, Theme } from '@mui/material/styles';
import React from 'react';
import { BannerIconWrapper, BannerRoot } from './styles';

type OverlayBannerProps = {
  position?: 'top' | 'bottom';
  icon?: React.ReactNode;
  children: React.ReactNode;
  offset?: string;
  sx?: SxProps<Theme>;
};

export const OverlayBanner = ({
  position = 'bottom',
  icon,
  children,
  offset = '2.4rem',
  sx,
}: OverlayBannerProps) => {
  const ownerState = position === 'bottom' ? { bottom: offset } : { top: offset };

  return (
    <BannerRoot ownerState={ownerState} sx={sx}>
      {/* Leading icon */}
      {icon && <BannerIconWrapper>{icon}</BannerIconWrapper>}

      {/* Content — text, bold spans, action icons, dividers */}
      {children}
    </BannerRoot>
  );
};
